import cn from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { Badge, ClickAwayListener, Fade } from '@mui/material';

import { PiSpeakerHigh } from 'react-icons/pi';
import { HiOutlineMicrophone } from 'react-icons/hi';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { BsCameraVideo } from 'react-icons/bs';

import { OSelect, OTooltip } from '@src/shared';
import { Emojis, MediaRequestCta } from '@src/components';
import { LoaderIcon } from '@src/assets/icons';

import useFooterActionsConfig, { TFooterActionConfig, TFooterActionId } from '../../useFooterActionsConfig';

import ThreeDotsPopper from './partials/ThreeDotsPopper';
import LeaveMeetingPopper, { useLeaveMeetingPopper } from './partials/LeaveMeetingPopper';

import { useRoomContext } from '@src/contexts';

import './roomFooterWeb.scss';

const RoomFooterWeb = ({ isDocumentPIP = false, pipWindow }: { isDocumentPIP?: boolean; pipWindow?: Window }) => {
  const { isHostOrCohost } = useRoomContext();
  const { leaveForMe } = useLeaveMeetingPopper(pipWindow);

  const {
    webCenter,
    webRight,
    handleEmojiClick,
    hasActiveLimitedPolls,
    isMicExtensionOpen,
    setIsMicExtensionOpen,
    isVideoExtensionOpen,
    setIsVideoExtensionOpen,
    speakerDevices,
    audioInputDevices,
    selectedAudio,
    selectedSpeaker,
    videoInputDevices,
    selectedCamera,
    handleChangeMediaDevice,
  } = useFooterActionsConfig();
  const [pipWindowWidth, setPipWindowWidth] = useState(() => pipWindow?.innerWidth ?? window.innerWidth);

  useEffect(() => {
    if (!isDocumentPIP || !pipWindow) return;

    const handleResize = () => setPipWindowWidth(pipWindow.innerWidth);
    handleResize();
    pipWindow.addEventListener('resize', handleResize);

    return () => {
      pipWindow.removeEventListener('resize', handleResize);
    };
  }, [isDocumentPIP, pipWindow]);

  const pipVisibility = useMemo<Partial<Record<TFooterActionId, boolean>> & { divider: boolean }>(() => {
    const showShareScreen = pipWindowWidth > 290;
    const showRaiseHand = pipWindowWidth > 338;
    const showReactions = pipWindowWidth > 395;
    const showChat = pipWindowWidth > 700;

    return {
      divider: false,
      mic: true,
      camera: true,
      reactions: showReactions,
      shareScreen: showShareScreen,
      raiseHand: showRaiseHand,
      threeDots: true,
      endCall: true,
      chat: showChat,
    };
  }, [pipWindowWidth]);

  const renderAction = (action: TFooterActionConfig) => {
    const isCapitalized = !!action.web?.capitalizeTooltip;

    // Handle custom renders
    if (action.web?.customRender === 'emojis') {
      return (
        <div
          key={action.id}
          className={
            isDocumentPIP ? 'icon-container emoji-wrapper-pip' : action.web.containerClass || 'relative-container'
          }
        >
          <Emojis onSelect={handleEmojiClick} />
        </div>
      );
    }

    if (action.web?.customRender === 'threeDots') {
      return isDocumentPIP ? (
        <ThreeDotsPopper key={action.id} />
      ) : (
        <div key={action.id} className="icon-container">
          <ThreeDotsPopper />
        </div>
      );
    }

    if (action.id === 'endCall' && isDocumentPIP) {
      if (isHostOrCohost) {
        return <LeaveMeetingPopper key={action.id} pipWindow={pipWindow} />;
      }

      return (
        <OTooltip key={action.id} title={action.tooltip || 'Leave meeting'}>
          <button className={cn('icon-container', 'red')} onClick={leaveForMe}>
            {action.icon}
          </button>
        </OTooltip>
      );
    }

    // Media buttons (mic/cam) with extensions
    if (action.web?.variant === 'media') {
      const badge = action.web?.badge;
      const cta = action.web?.cta;
      const mediaExt = action.web?.mediaExtension;
      const isClosed = action.web?.isClosed || false;

      return (
        <div key={action.id} className="media-button-container">
          {!isDocumentPIP && mediaExt && !mediaExt.show && (
            <button onClick={mediaExt.onToggle} className="media-extension">
              {mediaExt.isOpen ? (
                <FaChevronDown className="extension-select" size={15} />
              ) : (
                <FaChevronUp className="extension-select" size={15} />
              )}
            </button>
          )}

          <OTooltip isCapitalized={isCapitalized} title={action.tooltip || ''}>
            <div
              ref={action.ref}
              className={cn('icon-container', {
                closed: isClosed,
                disabled: action.isDisabled,
              })}
              onClick={() => action.onClick?.()}
            >
              {action.icon}
              {badge?.show && <Badge badgeContent={badge.content} className={badge.className || ''} />}
            </div>
          </OTooltip>

          {cta && (
            <MediaRequestCta
              hostName={cta.hostName}
              message={cta.message}
              buttonLabel={cta.buttonLabel}
              anchorRef={cta.anchorRef}
              open={true}
              onConfirm={cta.onConfirm}
              roleLabel={cta.roleLabel}
              onClose={cta.onClose}
              request={cta.request}
            />
          )}
        </div>
      );
    }

    // Loader handling (e.g. share screen)
    const loader = action.web?.loader;
    const iconContent = loader?.show ? (
      <div className={loader.className || ''}>
        <LoaderIcon />
      </div>
    ) : (
      action.icon
    );

    // Count display handling (e.g. attendees)
    const countDisplay = action.web?.countDisplay;

    // Determine if disabled
    const isDisabledByLimitedPolls = action.web?.disableOnLimitedPolls && hasActiveLimitedPolls;
    const isDisabled = action.isDisabled || isDisabledByLimitedPolls;

    // Determine onClick
    const handleClick = () => {
      if (!isDisabled) {
        action.onClick?.();
      }
    };

    const className = cn('icon-container', action.web?.containerClass || '', {
      active: action.isActive,
      disabled: isDisabled,
      red: action.web?.variant === 'danger',
    });

    return (
      <OTooltip key={action.id} isCapitalized={isCapitalized} title={action.tooltip || ''}>
        <button className={className} onClick={handleClick}>
          {action.hasNotification && <div className="notification-indicator" />}
          {iconContent}
          {countDisplay?.show && (
            <div
              className={cn(countDisplay.className || '', { [countDisplay.activeClassName || '']: action.isActive })}
            >
              {countDisplay.value}
            </div>
          )}
        </button>
      </OTooltip>
    );
  };

  const renderWebCenterActions = () => {
    if (!isDocumentPIP) {
      return webCenter.map((action) => (
        <Fragment key={action.id}>
          {renderAction(action)}
          {action.web?.showDividerAfter && <div className="divider" />}
        </Fragment>
      ));
    }

    const webCenterById = new Map(webCenter.map((action) => [action.id, action]));
    const pipOrder: TFooterActionId[] = [
      'mic',
      'camera',
      'shareScreen',
      'raiseHand',
      'reactions',
      'threeDots',
      'endCall',
    ];

    const renderPipItem = (key: string, node: React.ReactNode, isVisible: boolean) => (
      <Fade key={key} in={isVisible} timeout={150} appear={false} unmountOnExit>
        <div className="pip-fade-item">{node}</div>
      </Fade>
    );

    return pipOrder
      .map((id) => webCenterById.get(id))
      .filter((action): action is TFooterActionConfig => !!action)
      .flatMap((action) => {
        const items = [renderPipItem(action.id, renderAction(action), pipVisibility[action.id] ?? false)];

        // Add separator after reactions button (before threeDots)
        if (action.id === 'reactions') {
          items.push(
            <Fade key="pip-separator" in={true} timeout={150} appear={false}>
              <div className="pip-fade-item">
                <div className="divider" />
              </div>
            </Fade>
          );
        }

        return items;
      });
  };

  const renderWebRightActions = () => {
    if (!isDocumentPIP) {
      return webRight.map(renderAction);
    }

    const showChat = pipWindowWidth > 700;
    if (!showChat) return null;

    const chatAction = webRight.find((action) => action.id === 'chat');
    if (!chatAction) return null;

    return (
      <Fade in={showChat} timeout={150} appear={false} unmountOnExit>
        <div className="pip-fade-item">{renderAction(chatAction)}</div>
      </Fade>
    );
  };

  return (
    <div className={cn('meet-room-footer', { 'pip-compact': isDocumentPIP })}>
      {isDocumentPIP ? (
        pipWindowWidth > 700 && <div className="footer-right-container">{renderWebRightActions()}</div>
      ) : (
        <div className="footer-right-container">{webRight.map(renderAction)}</div>
      )}

      <div className="footer-left-container">
        <div className="icons-container">
          {renderWebCenterActions()}

          {/* Floating Mic select */}
          {isMicExtensionOpen && (
            <ClickAwayListener
              mouseEvent={'onMouseUp'}
              onClickAway={() => {
                setIsMicExtensionOpen(false);
              }}
            >
              <div className="audio-devices-container mic">
                <OSelect
                  items={audioInputDevices.map(({ deviceId, label }) => ({ value: deviceId, label }))}
                  onChange={(id) => {
                    const mediaDevice = audioInputDevices.find(({ deviceId }) => deviceId === id);
                    mediaDevice && handleChangeMediaDevice(mediaDevice, 'audio');
                  }}
                  selectedValue={selectedAudio}
                  icon={<HiOutlineMicrophone size={20} color="white" />}
                  classes={{
                    selectContainer: 'settings-select',
                  }}
                  isDisabled={!audioInputDevices.length}
                  tooltipContent={!audioInputDevices.length ? 'Allow mic access' : ''}
                  size={'x-medium'}
                />
                <OSelect
                  items={speakerDevices.map(({ deviceId, label }) => ({ value: deviceId, label }))}
                  onChange={(id) => {
                    const mediaDevice = speakerDevices.find(({ deviceId }) => deviceId === id);
                    mediaDevice && handleChangeMediaDevice(mediaDevice, 'speaker');
                  }}
                  selectedValue={selectedSpeaker}
                  classes={{
                    selectContainer: 'settings-select',
                  }}
                  icon={<PiSpeakerHigh size={20} color="white" />}
                  isDisabled={!speakerDevices.length}
                  tooltipContent={!speakerDevices.length ? 'Allow sound access' : ''}
                  size={'x-medium'}
                />
              </div>
            </ClickAwayListener>
          )}

          {/* Floating Cam select */}
          {isVideoExtensionOpen && (
            <ClickAwayListener
              mouseEvent={'onMouseUp'}
              onClickAway={() => {
                setIsVideoExtensionOpen(false);
              }}
            >
              <div className="audio-devices-container cam">
                <OSelect
                  items={videoInputDevices.map(({ deviceId, label }) => ({ value: deviceId, label }))}
                  onChange={(id) => {
                    const mediaDevice = videoInputDevices.find(({ deviceId }) => deviceId === id);
                    mediaDevice && handleChangeMediaDevice(mediaDevice, 'video');
                  }}
                  classes={{
                    selectContainer: 'settings-select',
                  }}
                  icon={<BsCameraVideo size={20} color="white" />}
                  selectedValue={selectedCamera}
                  isDisabled={!videoInputDevices.length}
                  tooltipContent={!videoInputDevices.length ? 'Allow camera access' : ''}
                  size={'x-medium'}
                />
              </div>
            </ClickAwayListener>
          )}
        </div>

        {!isDocumentPIP && (
          <div className="footer-text">
            Meet • Powered by <span>v</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomFooterWeb;
